import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { removeUser } from "./utils/userSlice";
import useGetTotalInfluencers from "./utils/useGetTotalInfluencers";
import { addInfluencers, setLoading } from "./utils/allInfluencersSlice";
import { jwtDecode } from "jwt-decode";
import "./App.scss";
import { ThemeProvider } from "@mui/material";
import theme from "./components/theme";
import Loader from "./components/Loader";
const Login = lazy(() => import("./components/Login"));
const AddData = lazy(() => import("./components/AddData"));
const AddDataHome = lazy(() => import("./components/AddDataHome"));
const InfluencerProfile = lazy(
  () => import("./components/influencer-profile/InfluencerProfile"),
);
const Dashboard = lazy(() => import("./components/dashboard/Dashboard"));
const Influencers = lazy(() => import("./components/influencers/Influencers"));
const Settings = lazy(() => import("./components/settings/Settings"));
const Password = lazy(() => import("./components/settings/Password"));
const BackupRecovery = lazy(
  () => import("./components/settings/BackupRecovery"),
);
const BulkUploadData = lazy(() => import("./components/BulkUploadData"));
const UsersList = lazy(() => import("./components/create-user/UsersList"));
const CreateUser = lazy(() => import("./components/create-user/CreateUser"));
const UserAccess = lazy(() => import("./components/create-user/UserAccess"));
const Campaigns = lazy(() => import("./components/campaigns/Campaigns"));
const ViewAllCampaign = lazy(
  () => import("./components/campaigns/ViewAllCampaign"),
);
const TrashedItems = lazy(() => import("./components/settings/TrashedItems"));
const CampaignDetails = lazy(
  () => import("./components/campaigns/CampaignDetails"),
);
const Brands = lazy(() => import("./components/brands/Brands"));
const BrandDetails = lazy(() => import("./components/brands/BrandDetails"));
const NoAccess = lazy(() => import("./components/NoAccess"));
const Agreements = lazy(() => import("./components/agreements/Agreements"));

function App() {
  const dispatch = useDispatch();
  const user = useSelector((store) => store.user);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token"),
  );

  useEffect(() => {
    const token = user?.token;
    setIsAuthenticated(!!token);
  }, []);

  const [isLoading, allInfluencers] = useGetTotalInfluencers(isAuthenticated);

  useEffect(() => {
    if (isLoading !== undefined) {
      dispatch(setLoading(isLoading));
    }

    if (allInfluencers) {
      dispatch(addInfluencers(allInfluencers));
    }
  }, [isLoading, allInfluencers, dispatch]);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      const decodedToken = jwtDecode(token);
      if (Date.now() >= decodedToken.exp * 1000) {
        handleLogout();
      } else {
        const expiryTime = decodedToken.exp * 1000 - Date.now();
        setTimeout(() => {
          handleLogout();
        }, expiryTime);
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    dispatch(removeUser());
    setIsAuthenticated(false);
  };

  const { userData: { permissions: { addData, editData } = {} } = {} } =
    user || {};

  const router = createBrowserRouter([
    {
      path: "/",
      element: isAuthenticated ? (
        user?.userData?.permissions?.showFullDashboard ? (
          <Suspense fallback={<Loader />}>
            <Dashboard onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/influencers" />
        )
      ) : (
        <Navigate to="/login" />
      ),
    },
    {
      path: "/influencers",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <Influencers onLogout={handleLogout} />
        </Suspense>
      ) : (
        <Navigate to="/login" />
      ),
    },
    {
      path: "/brands",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <Brands onLogout={handleLogout} />
        </Suspense>
      ) : (
        <Navigate to="/login" />
      ),
    },
    {
      path: "/brand-details/:id",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <BrandDetails onLogout={handleLogout} />
        </Suspense>
      ) : (
        <Navigate to="/login" />
      ),
    },
    {
      path: "/login",
      element: !isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <Login onLogin={handleLogin} />
        </Suspense>
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/add-data",
      element: isAuthenticated ? (
        user?.userData?.isAdmin ||
        user?.userData?.permissions?.uploadFile ||
        user?.userData?.permissions?.addData ? (
          <Suspense fallback={<Loader />}>
            <AddDataHome onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/add-data/manually",
      element: isAuthenticated ? (
        addData ? (
          <Suspense fallback={<Loader />}>
            <AddData onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/add-data/bulk-upload",
      element: isAuthenticated ? (
        user?.userData?.isAdmin || user?.userData?.permissions?.uploadFile ? (
          <Suspense fallback={<Loader />}>
            <BulkUploadData onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/influencer-profile/:id",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <InfluencerProfile />
        </Suspense>
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/edit/:id",
      element: isAuthenticated ? (
        editData ? (
          <Suspense fallback={<Loader />}>
            <AddData onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/settings/basic-informations",
      element: isAuthenticated ? (
        user?.userData?.permissions?.settings || user?.userData?.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <Settings onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/settings/password",
      element: isAuthenticated ? (
        user?.userData?.permissions?.settings || user?.userData?.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <Password onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/settings/backup-and-recovery",
      element: isAuthenticated ? (
        user?.userData?.permissions?.settings || user?.userData?.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <BackupRecovery onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/settings/trash",
      element: isAuthenticated ? (
        user?.userData?.permissions?.settings || user?.userData?.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <TrashedItems onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/users",
      element: isAuthenticated ? (
        user.userData.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <UsersList onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/users/create-user",
      element: isAuthenticated ? (
        user.userData.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <CreateUser onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/users/edit-user/:id",
      element: isAuthenticated ? (
        user.userData.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <CreateUser onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/users/user-access/:id",
      element: isAuthenticated ? (
        user.userData.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <UserAccess onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/campaign",
      element: isAuthenticated ? (
        <Navigate to="/campaign/create-campaign" />
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/campaign/create-campaign",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <Campaigns onLogout={handleLogout} />
        </Suspense>
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/campaign/all-campaigns",
      element: isAuthenticated ? (
        user?.userData?.isAdmin || user?.userData?.permissions?.viewCampaign ? (
          <Suspense fallback={<Loader />}>
            <ViewAllCampaign onLogout={handleLogout} />
          </Suspense>
        ) : (
          <Navigate to="/no-access" />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/campaign/edit-campaign/:id",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <Campaigns onLogout={handleLogout} />
        </Suspense>
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/no-access",
      element: isAuthenticated ? (
        <Suspense fallback={<Loader />}>
          <NoAccess />
        </Suspense>
      ) : (
        <Navigate to={"/"} />
      ),
    },
    {
      path: "/campaign/campaign-details/:id",
      element: isAuthenticated ? (
        user?.userData?.isAdmin ||
        user?.userData?.permissions?.viewCampaignDetails ? (
          <Suspense fallback={<Loader />}>
            <CampaignDetails onLogout={handleLogout} />
          </Suspense>
        ) : (
          <NoAccess />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
    {
      path: "/creators-agreements",
      element: isAuthenticated ? (
        user?.userData?.isAdmin ? (
          <Suspense fallback={<Loader />}>
            <Agreements onLogout={handleLogout} />
          </Suspense>
        ) : (
          <NoAccess />
        )
      ) : (
        <Navigate to="/" />
      ),
    },
  ]);

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <RouterProvider router={router} />
      </ThemeProvider>
    </div>
  );
}

export default App;
